import React, { useEffect } from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { isValid } from 'date-fns';
import useDeleteSingleReport from 'Hooks/query-hooks/reports/useDeleteSingleReport';
import { DateService } from 'Services';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import styles from './DeleteReportModal.css';

export type ReportRowValues = {
    id: number;
    name: string;
    createdAt: string;
    createdBy: string;
    source?: 'window' | 'menu'; // track the source of the click. window: Report window; menu: row ellipsis menu
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    rowValues: ReportRowValues;
    reportType: ReportType;
};

const DeleteReportModal = ({ isOpen, onClose, rowValues, onSuccess, reportType }: Props) => {
    const { t } = useTranslation();
    const { id, name, createdAt, createdBy } = rowValues;

    const reportCreatedAt = isValid(new Date(createdAt)) ? DateService.getDate(createdAt) : '';
    const reportCreatedBy = createdBy && createdBy !== '' ? createdBy : '';
    const deleteSingleReport = useDeleteSingleReport(id);

    const handleTrackDeleteReport = React.useCallback(() => {
        let clickIdentifier = '';
        let text = '';
        if (reportType === 'merchantsummary') {
            if (rowValues.source === 'menu') {
                clickIdentifier = 'Merchant Summary Reports - Delete';
                text = 'Merchant Summary Menu Delete';
            } else if (rowValues.source === 'window') {
                clickIdentifier = 'Merchant Summary Report - Delete Report';
                text = 'Delete Merchant Summary Report';
            }
        } else if (reportType === 'performance') {
            if (rowValues.source === 'menu') {
                clickIdentifier = 'Rule Performance Reports - Delete';
                text = 'Rules Performance Menu Delete';
            } else if (rowValues.source === 'window') {
                clickIdentifier = 'Rule Performance Report - Delete Report';
                text = 'Delete Rule Performance Report';
            }
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    }, [reportType, rowValues.source]);

    useEffect(() => {
        if (deleteSingleReport?.isSuccess) {
            handleTrackDeleteReport();
            onClose();
            onSuccess?.();
        }
    }, [deleteSingleReport, onClose, onSuccess, handleTrackDeleteReport]);

    return (
        <Modal
            dataTestId='delete-report-modal'
            isOpen={isOpen}
            closeModal={onClose}
            status='warning'
            containerClass={styles.modalContainer}
        >
            <Modal.Title id='delete-report-title' title={t('Delete Report')} statusIcon='warning' onClose={onClose} />
            <div className={styles.deleteWarningContent}>
                <div className={styles.reportInfo}>
                    <span>{t('Are you sure you want to delete this report', { reportType })}</span>
                    <span className={styles.boldedFont}>{name}</span>
                    <span>&nbsp;{`${t('created')} ${t('on')}`}&nbsp;</span>
                    <span className={styles.boldedFont}>{reportCreatedAt}&nbsp;</span>
                    <span>{`${t('by')}`}&nbsp;</span>
                    <span className={styles.boldedFont}>{reportCreatedBy}</span>
                    <span>{t('?')}</span>
                </div>
                {t('This cannot be undone')}
            </div>
            <Modal.Footer
                id='delete-report-footer'
                onAction={() => deleteSingleReport.mutate()}
                onActionText={t('Delete')}
                onClose={onClose}
                onCloseText={t('Cancel')}
            />
        </Modal>
    );
};

export default DeleteReportModal;
